import { Box, Text, HStack, VStack, IconButton, Tooltip, useDisclosure, Input } from '@chakra-ui/react';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import WaveSurfer from 'wavesurfer.js';
import { useAudioContext } from '../context/AudioContext';
import { useNotes } from '../context/NotesContext';
import { useDisplayNames } from '../context/DisplayNamesContext';
import { useRegions, Region } from '../context/RegionsContext';
import { useSync } from '../features/sync/context/SyncContext';
import { FaStickyNote, FaTrash, FaEyeSlash, FaEdit } from 'react-icons/fa';
import NotesModal from './NotesModal';
import { isIOS, isMobileDevice } from '../utils/deviceDetection';
import { connectMobileMediaElement } from '../utils/mobileAudioContextManager';
import { SyncMarker } from '../features/sync/components/SyncMarker';

const TooltipKeyBox = ({ children }: { children: React.ReactNode }) => (
  <Box
    as="span"
    px={2}
    py={0.5}
    bg="gray.700"
    borderRadius="md"
    fontSize="sm"
    border="1px solid"
    borderColor="gray.500"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    minW="24px"
    h="24px"
    color="gray.100"
    fontWeight="medium"
    _hover={{
      bg: "gray.600",
    }}
  >
    {children}
  </Box>
);

interface AudioPlayerProps {
  id?: string;
  trackNumber: number;
  file?: File;
  originalUrl?: string;
  shareableUrl?: string;
  'data-player-number'?: number;
  onWavesurferReady?: (wavesurfer: WaveSurfer) => void;
  onRemove?: (trackNumber: number) => void;
  isDragged?: boolean;
  isEmbed?: boolean;
}

// Helper to check if any track is over 10 minutes
const hasLongTracks = (audioElements: Map<number, HTMLAudioElement>): boolean => {
  for (const audio of audioElements.values()) {
    if (audio.duration && audio.duration > 600) { // 600 seconds = 10 minutes
      return true;
    }
  }
  return false;
};

const AudioPlayer: React.FC<AudioPlayerProps> = ({ 
  id,
  trackNumber, 
  file,
  originalUrl,
  shareableUrl,
  'data-player-number': dataPlayerNumber,
  onWavesurferReady,
  onRemove,
  isDragged = false,
  isEmbed = false,
  ...props
}) => {
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const isInitializedRef = useRef(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [fileName, setFileName] = useState<string>('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [hasExpandedPanel, setHasExpandedPanel] = useState(false);
  const endedHandlerRef = useRef<(() => void) | null>(null);
  
  // Drag-to-set-loop state (desktop only)
  const isDraggingLoopRef = useRef(false);
  const dragStartXRef = useRef<number | null>(null);
  const dragStartTimeRef = useRef<number | null>(null);
  // Preview loop region during drag (visual only, not applied until release)
  const [previewLoopStart, setPreviewLoopStart] = useState<number | null>(null);
  const [previewLoopEnd, setPreviewLoopEnd] = useState<number | null>(null);
  // RAF for smooth preview updates
  const previewUpdateRafRef = useRef<number | null>(null);
  const pendingPreviewRef = useRef<{ start: number; end: number } | null>(null);
  
  // Loop handle drag state (desktop only)
  const isDraggingHandleRef = useRef<'start' | 'end' | null>(null);
  const handleDragStartTimeRef = useRef<number | null>(null);
  const pendingStartTimeRef = useRef<number | null>(null);
  const lastUpdateTimeRef = useRef<number>(0);
  const lastUpdateValueRef = useRef<number | null>(null);

  // Check for expanded panel availability periodically
  useEffect(() => {
    const checkExpandedPanel = () => {
      const expandedNotePanel = (window as any).__expandedNotePanel;
      const isAvailable = expandedNotePanel && expandedNotePanel.trackNumber === trackNumber;
      setHasExpandedPanel(isAvailable);
    };

    // Check immediately
    checkExpandedPanel();

    // Set up an interval to check periodically (for dynamic changes)
    const interval = setInterval(checkExpandedPanel, 100);

    return () => clearInterval(interval);
  }, [trackNumber]);
  const { 
    registerAudio,
    selectedPlayer,
    selectPlayer,
    gainOffsets,
    audioElements,
    isLooping,
    loopStart,
    loopEnd,
    loopState,
    removePlayer,
    isPlaying,
    playAll,
    pauseAll,
    blindTestIncludedPlayers,
    toggleBlindTestIncluded,
    getPlayerLoopPoints,
    setLastStartPositions,
    seekToTime,
    resetGainForPlayer,
    resetAllGains,
    registerWaveSurfer,
    setLoopStart,
    setLoopEnd,
    disableLoop,
  } = useAudioContext();
  const { syncPoints, hasJumpedToSyncPoints } = useSync();
  const { getNote } = useNotes();
  const { getCustomDisplayName, hasCustomDisplayName, setDisplayName } = useDisplayNames();
  const { getRegions } = useRegions();
  
  // Get note content for timestamp extraction
  const noteContent = id ? getNote(id) : '';
  
  // State for region tooltip that follows mouse
  const [hoveredRegion, setHoveredRegion] = useState<Region | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const regionOverlayRef = useRef<HTMLDivElement>(null);
  
  // State for timestamp/textstamp tooltip
  const [hoveredTimestamp, setHoveredTimestamp] = useState<{ time: number; type: 'timestamp' | 'textstamp'; text?: string } | null>(null);
  const [timestampTooltipPosition, setTimestampTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const timestampOverlayRef = useRef<HTMLDivElement>(null);
  
  // Interface for timestamp/textstamp data
  interface TimestampData {
    time: number;
    type: 'timestamp' | 'textstamp';
    text?: string;
  }
  
  // Extract timestamps and textstamps from notes HTML
  const timestampsAndTextstamps = useMemo<TimestampData[]>(() => {
    if (!id || !noteContent) return [];
    
    // Create a temporary DOM element to parse the HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = noteContent;
    
    const stamps: TimestampData[] = [];
    
    // Find all timestamp elements (class="timestamp")
    const timestampElements = tempDiv.querySelectorAll('.timestamp');
    timestampElements.forEach((element) => {
      const timestampAttr = element.getAttribute('data-timestamp');
      if (timestampAttr) {
        const time = parseFloat(timestampAttr);
        if (!isNaN(time)) {
          stamps.push({
            time,
            type: 'timestamp',
          });
        }
      }
    });
    
    // Find all textstamp elements (class="chord")
    const chordElements = tempDiv.querySelectorAll('.chord');
    chordElements.forEach((element) => {
      const timestampAttr = element.getAttribute('data-timestamp');
      if (timestampAttr) {
        const time = parseFloat(timestampAttr);
        if (!isNaN(time)) {
          const text = element.textContent || '';
          stamps.push({
            time,
            type: 'textstamp',
            text,
          });
        }
      }
    });
    
    // Sort by time
    stamps.sort((a, b) => a.time - b.time);
    
    return stamps;
  }, [id, noteContent]);
  
  // Format time based on whether any tracks are over 10 minutes
  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    const useLeadingZero = hasLongTracks(audioElements);
    const minutesStr = useLeadingZero 
      ? minutes.toString().padStart(2, '0')
      : minutes.toString();
    return `${minutesStr}:${seconds.toString().padStart(2, '0')}`;
  };
  
  // State for editing display name (desktop only)
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingNameValue, setEditingNameValue] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const originalNameRef = useRef<string>('');
  
  // Focus input when editing starts
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [isEditingName]);

  // Initialize WaveSurfer only once
  useEffect(() => {
    if (isInitializedRef.current || !waveformRef.current) return;
    isInitializedRef.current = true;

    let isMounted = true;
    let wavesurfer: WaveSurfer | null = null;
    let objectUrl: string | null = null;

    const initializeWaveSurfer = async () => {
      try {
        // In embed mode, use MediaElement backend to avoid AudioContext suspension issues
        // MediaElement backend uses native HTML5 audio which works better in iframes
        const backend = isIOS() || isEmbed ? 'MediaElement' : 'WebAudio';
        console.log(`Initializing WaveSurfer with ${backend} backend for track ${trackNumber}${isEmbed ? ' (embed mode)' : ''}`);
        
        wavesurfer = WaveSurfer.create({
          container: waveformRef.current!,
          waveColor: 'rgba(160, 174, 192, 0.5)',
          progressColor: 'rgba(221, 107, 32, 0.85)',
          cursorColor: '#DD6B20',
          barWidth: 2,
          barRadius: 2,
          cursorWidth: 1,
          height: 32,
          barGap: 1,
          normalize: true,
          backend,
          hideScrollbar: true,
          interact: selectedPlayer === trackNumber
        });

        wavesurferRef.current = wavesurfer;

        wavesurfer.on('ready', async () => {
          if (!isMounted || !wavesurfer) return;
          
          const audioElement = wavesurfer.getMediaElement();
          if (audioElement) {
            // Set title and filename
            if (file) {
              audioElement.title = file.name;
              setFileName(file.name);
            }
            audioElement.loop = false;
            
            // Add event listener for when audio ends - restart from beginning for all tracks
            const handleEnded = () => {
              // Reset to beginning
              audioElement.currentTime = 0;
              // Continue playing if it was playing (ended event only fires if it was playing)
              // Use a small delay to ensure the currentTime change has taken effect
              setTimeout(() => {
                audioElement.play().catch(err => {
                  console.warn(`Failed to restart playback after track end for player ${trackNumber}:`, err);
                });
              }, 0);
            };
            endedHandlerRef.current = handleEnded;
            audioElement.addEventListener('ended', handleEnded);
            
            // Initialize WebAudio context on mobile
            if (isMobileDevice()) {
              try {
                await connectMobileMediaElement(trackNumber, audioElement, id);
              } catch (error) {
                console.warn(`Failed to initialize WebAudio for player ${trackNumber}:`, error);
              }
            }
            
            // Register the audio element with the original URL for sharing
            registerAudio(trackNumber, audioElement);
            
            // Register WaveSurfer instance for mono processing (desktop)
            // Wait a bit to ensure backend is fully initialized
            setTimeout(() => {
              if (wavesurfer) {
                registerWaveSurfer(trackNumber, wavesurfer);
              }
            }, 200);
            
            // Ensure the ready state is properly communicated
            if (onWavesurferReady) {
              console.log(`Notifying ready state for track ${trackNumber}`);
              onWavesurferReady(wavesurfer);
            }
            
            setDuration(wavesurfer.getDuration());
            console.log(`WaveSurfer ready for track ${trackNumber} with ${backend} backend`);

            // Safe to revoke object URL after media is ready
            if (objectUrl) {
              try {
                URL.revokeObjectURL(objectUrl);
              } catch {}
              objectUrl = null;
            }
          }
        });

        wavesurfer.on('destroy', () => {
          console.log(`WaveSurfer destroyed for track ${trackNumber}`);
        });

        wavesurfer.on('audioprocess', () => {
          if (!isMounted || !wavesurfer) return;
          const currentTime = wavesurfer.getCurrentTime();
          setCurrentTime(currentTime);
        });

        if (file) {
          objectUrl = URL.createObjectURL(file);
          // Do not await here; some backends return void. Revoke after 'ready' or on cleanup.
          wavesurfer.load(objectUrl);
        }
      } catch (error) {
        console.error(`Failed to initialize WaveSurfer for track ${trackNumber}:`, error);
      }
    };

    initializeWaveSurfer();

    return () => {
      isMounted = false;
      if (wavesurfer) {
        const audioElement = wavesurfer.getMediaElement();
        if (audioElement && endedHandlerRef.current) {
          audioElement.removeEventListener('ended', endedHandlerRef.current);
          endedHandlerRef.current = null;
        }
        wavesurfer.destroy();
        wavesurfer = null;
      }
      if (objectUrl) {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {}
        objectUrl = null;
      }
      isInitializedRef.current = false;
    };
  }, []);

  // Handle looping in a separate effect
  useEffect(() => {
    const wavesurfer = wavesurferRef.current;
    if (!wavesurfer) return;

    let lastLoopTimestamp = 0; // Track when we last performed a loop operation (in milliseconds)
    const LOOP_COOLDOWN_MS = 100; // Minimum time between loops (100ms)

    const handleLoop = () => {
      const currentTime = wavesurfer.getCurrentTime();
      const duration = wavesurfer.getDuration();
      
      if (!duration || duration <= 0) return;
      
      // Use player-specific loop points if available, otherwise use global loop points
      const playerLoopPoints = getPlayerLoopPoints(trackNumber);
      const effectiveLoopStart = playerLoopPoints?.start ?? loopStart;
      const effectiveLoopEnd = playerLoopPoints?.end ?? loopEnd;
      
      // Prevent rapid re-triggering by checking cooldown
      const now = Date.now();
      const timeSinceLastLoop = now - lastLoopTimestamp;
      const shouldLoop = isLooping && currentTime >= effectiveLoopEnd && 
                        timeSinceLastLoop >= LOOP_COOLDOWN_MS;
      
      if (shouldLoop) {
        const audioElement = wavesurfer.getMediaElement();
        if (audioElement) {
          // Ensure we're seeking to a valid position
          const seekTime = Math.max(0, Math.min(effectiveLoopStart, duration));
          
          // Store whether we were playing before the seek
          const wasPlaying = isPlaying && !audioElement.paused;
          
          // Update wavesurfer first
          const seekRatio = seekTime / duration;
          wavesurfer.seekTo(seekRatio);
          
          // Then set the time on the audio element
          audioElement.currentTime = seekTime;
          
          // Ensure playback continues if it was playing
          // Use requestAnimationFrame to ensure the seek has processed
          if (wasPlaying) {
            requestAnimationFrame(() => {
              if (audioElement.paused && isPlaying) {
                audioElement.play().catch(err => {
                  console.warn('Failed to resume playback after loop:', err);
                });
              }
            });
          }
          
          lastLoopTimestamp = now;
        }
      }
    };

    // Check loop points more frequently for smoother looping
    const checkInterval = setInterval(handleLoop, 10);
    wavesurfer.on('audioprocess', handleLoop);

    const audioElement = wavesurfer.getMediaElement();
    if (audioElement) {
      audioElement.addEventListener('timeupdate', handleLoop);
      return () => {
        clearInterval(checkInterval);
        audioElement.removeEventListener('timeupdate', handleLoop);
        wavesurfer.un('audioprocess', handleLoop);
      };
    }

    return () => {
      clearInterval(checkInterval);
      wavesurfer.un('audioprocess', handleLoop);
    };
  }, [isLooping, loopStart, loopEnd, getPlayerLoopPoints, trackNumber, isPlaying]);

  // Handle seek synchronization
  useEffect(() => {
    const wavesurfer = wavesurferRef.current;
    if (!wavesurfer) return;

    const handleInteraction = () => {
      // Don't seek if we're dragging to set loop points or dragging a handle
      if (isDraggingLoopRef.current || isDraggingHandleRef.current !== null) return;
      
      const currentTime = wavesurfer.getCurrentTime();
      
      // Use seekToTime with sync context to respect offsets
      seekToTime(currentTime, syncPoints, hasJumpedToSyncPoints);
      
      // Update last start positions if playing
      if (isPlaying) {
        const newStartPositions = new Map<number, number>();
        audioElements.forEach((audio, playerNumber) => {
          newStartPositions.set(playerNumber, audio.currentTime);
        });
        setLastStartPositions(newStartPositions);
      }
    };

    // Listen for both click and touch events
    wavesurfer.on('interaction', handleInteraction);

    return () => {
      wavesurfer.un('interaction', handleInteraction);
    };
  }, [audioElements, isPlaying, setLastStartPositions, seekToTime, syncPoints, hasJumpedToSyncPoints]);

  // Drag-to-set-loop functionality (desktop only)
  useEffect(() => {
    // Only enable on desktop and for selected player
    if (isMobileDevice() || selectedPlayer !== trackNumber) {
      return;
    }

    const container = waveformRef.current;
    if (!container) return;

    const wavesurfer = wavesurferRef.current;
    if (!wavesurfer) return;

    const getTimeFromMouseX = (clientX: number): number => {
      const rect = container.getBoundingClientRect();
      const mouseX = clientX - rect.left;
      const percentage = Math.max(0, Math.min(1, mouseX / rect.width));
      return percentage * duration;
    };

    const handleMouseDown = (e: MouseEvent) => {
      // Only allow if this is the selected player
      if (selectedPlayer !== trackNumber) return;

      // Check if we're clicking on a loop handle (only if loop exists and not Alt+drag)
      if (isLooping && !e.altKey) {
        const playerLoopPoints = getPlayerLoopPoints(trackNumber);
        const effectiveLoopStart = playerLoopPoints?.start ?? loopStart;
        const effectiveLoopEnd = playerLoopPoints?.end ?? loopEnd;
        
        const clickTime = getTimeFromMouseX(e.clientX);
        const handleWidth = 8; // pixels tolerance for handle detection
        const handleWidthTime = (handleWidth / container.getBoundingClientRect().width) * duration;
        
        // Check if click is near loop start handle
        if (Math.abs(clickTime - effectiveLoopStart) < handleWidthTime) {
          e.preventDefault();
          e.stopPropagation();
          isDraggingHandleRef.current = 'start';
          handleDragStartTimeRef.current = effectiveLoopStart;
          return;
        }
        
        // Check if click is near loop end handle
        if (Math.abs(clickTime - effectiveLoopEnd) < handleWidthTime) {
          e.preventDefault();
          e.stopPropagation();
          isDraggingHandleRef.current = 'end';
          handleDragStartTimeRef.current = effectiveLoopEnd;
          return;
        }
      }

      // Only start drag-to-create-loop if Alt/Option key is held
      if (!e.altKey) return;

      e.preventDefault();
      e.stopPropagation();

      // If a loop already exists, dismiss it before creating a new one
      if (isLooping) {
        disableLoop();
      }

      const startTime = getTimeFromMouseX(e.clientX);
      dragStartXRef.current = e.clientX;
      dragStartTimeRef.current = startTime;
      isDraggingLoopRef.current = false; // Will be set to true once threshold is exceeded
      // Initialize preview loop region
      setPreviewLoopStart(startTime);
      setPreviewLoopEnd(null);
    };

    const handleMouseMove = (e: MouseEvent) => {
      // Handle loop handle dragging
      if (isDraggingHandleRef.current !== null) {
        const newTime = getTimeFromMouseX(e.clientX);
        const playerLoopPoints = getPlayerLoopPoints(trackNumber);
        const currentLoopStart = playerLoopPoints?.start ?? loopStart;
        const currentLoopEnd = playerLoopPoints?.end ?? loopEnd;
        
        if (isDraggingHandleRef.current === 'start') {
          // Don't allow start to go past end (with 0.1s minimum loop duration)
          const clampedTime = Math.max(0, Math.min(newTime, currentLoopEnd - 0.1));
          
          // Throttle updates to avoid glitching - only update every 16ms (~60fps)
          // and only if the value has changed significantly (more than 0.01s)
          const now = Date.now();
          const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
          const valueChanged = lastUpdateValueRef.current === null || 
                              Math.abs(clampedTime - lastUpdateValueRef.current) > 0.01;
          
          if (valueChanged && (timeSinceLastUpdate > 16 || lastUpdateValueRef.current === null)) {
            lastUpdateTimeRef.current = now;
            lastUpdateValueRef.current = clampedTime;
            pendingStartTimeRef.current = clampedTime;
            
            // Update start time, then immediately restore end time to keep loop active
            // This ensures the loop state stays 'inactive' (active) and isLooping stays true
            setLoopStart(clampedTime, syncPoints, hasJumpedToSyncPoints);
            // Immediately call setLoopEnd to restore loop state
            // setLoopEnd checks time > loopStartTime, and currentLoopEnd > clampedTime, so this should work
            setLoopEnd(currentLoopEnd, syncPoints, hasJumpedToSyncPoints);
          }
        } else if (isDraggingHandleRef.current === 'end') {
          // Don't allow end to go before start (with 0.1s minimum loop duration)
          const clampedTime = Math.min(duration, Math.max(newTime, currentLoopStart + 0.1));
          
          // Throttle updates to avoid glitching - only update every 16ms (~60fps)
          // and only if the value has changed significantly (more than 0.01s)
          const now = Date.now();
          const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
          const valueChanged = lastUpdateValueRef.current === null || 
                              Math.abs(clampedTime - lastUpdateValueRef.current) > 0.01;
          
          if (valueChanged && (timeSinceLastUpdate > 16 || lastUpdateValueRef.current === null)) {
            lastUpdateTimeRef.current = now;
            lastUpdateValueRef.current = clampedTime;
            setLoopEnd(clampedTime, syncPoints, hasJumpedToSyncPoints);
          }
        }
        return;
      }

      // Handle drag-to-create-loop
      if (dragStartXRef.current === null || dragStartTimeRef.current === null) return;

      const currentX = e.clientX;
      const deltaX = Math.abs(currentX - dragStartXRef.current);
      
      // Check if Alt/Option is still held
      if (!e.altKey) {
        // Cancel drag if Alt key is released
        dragStartXRef.current = null;
        dragStartTimeRef.current = null;
        isDraggingLoopRef.current = false;
        // Cancel any pending RAF update
        if (previewUpdateRafRef.current !== null) {
          cancelAnimationFrame(previewUpdateRafRef.current);
          previewUpdateRafRef.current = null;
        }
        pendingPreviewRef.current = null;
        setPreviewLoopStart(null);
        setPreviewLoopEnd(null);
        return;
      }

      // Only start dragging if movement exceeds threshold (5px)
      if (deltaX > 5) {
        if (!isDraggingLoopRef.current) {
          // First time exceeding threshold - mark as dragging
          isDraggingLoopRef.current = true;
        }

        // Update preview loop end as we drag (visual only, no expensive calculations)
        const endTime = getTimeFromMouseX(currentX);
        // Handle both forward and backward drags - swap start/end if needed
        let newStart: number;
        let newEnd: number;
        if (endTime > dragStartTimeRef.current) {
          // Dragging forward: start is dragStartTimeRef, end is current position
          newStart = dragStartTimeRef.current;
          newEnd = endTime;
        } else {
          // Dragging backward: start is current position, end is dragStartTimeRef
          newStart = endTime;
          newEnd = dragStartTimeRef.current;
        }
        
        // Store pending update
        pendingPreviewRef.current = { start: newStart, end: newEnd };
        
        // Use requestAnimationFrame for smooth, responsive updates
        if (previewUpdateRafRef.current === null) {
          previewUpdateRafRef.current = requestAnimationFrame(() => {
            if (pendingPreviewRef.current) {
              setPreviewLoopStart(pendingPreviewRef.current.start);
              setPreviewLoopEnd(pendingPreviewRef.current.end);
              pendingPreviewRef.current = null;
            }
            previewUpdateRafRef.current = null;
          });
        }
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      // Handle loop handle drag end
      if (isDraggingHandleRef.current !== null) {
        // If we were dragging the start handle, ensure the end is set one more time
        if (isDraggingHandleRef.current === 'start' && pendingStartTimeRef.current !== null) {
          const playerLoopPoints = getPlayerLoopPoints(trackNumber);
          const currentLoopEnd = playerLoopPoints?.end ?? loopEnd;
          if (currentLoopEnd > pendingStartTimeRef.current) {
            setLoopEnd(currentLoopEnd, syncPoints, hasJumpedToSyncPoints);
          }
        }
        isDraggingHandleRef.current = null;
        handleDragStartTimeRef.current = null;
        pendingStartTimeRef.current = null;
        lastUpdateTimeRef.current = 0;
        lastUpdateValueRef.current = null;
        return;
      }

      // Handle drag-to-create-loop end
      if (dragStartXRef.current === null || dragStartTimeRef.current === null) return;

      const wasDragging = isDraggingLoopRef.current;
      const startTime = dragStartTimeRef.current;
      
      // Get the final loop times from preview or mouse position
      const finalStartTime = previewLoopStart ?? startTime;
      const finalEndTime = previewLoopEnd ?? getTimeFromMouseX(e.clientX);
      
      // Reset drag state and preview
      dragStartXRef.current = null;
      dragStartTimeRef.current = null;
      isDraggingLoopRef.current = false;
      // Cancel any pending RAF update
      if (previewUpdateRafRef.current !== null) {
        cancelAnimationFrame(previewUpdateRafRef.current);
        previewUpdateRafRef.current = null;
      }
      pendingPreviewRef.current = null;
      setPreviewLoopStart(null);
      setPreviewLoopEnd(null);

      // If we were dragging, apply the actual loop (only now do expensive calculations)
      // Ensure start < end (should already be true from preview logic, but double-check)
      if (wasDragging && e.altKey && finalEndTime > finalStartTime) {
        setLoopStart(finalStartTime, syncPoints, hasJumpedToSyncPoints);
        setLoopEnd(finalEndTime, syncPoints, hasJumpedToSyncPoints);
      }
    };

    // Add event listeners
    container.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // Clean up any pending RAF
      if (previewUpdateRafRef.current !== null) {
        cancelAnimationFrame(previewUpdateRafRef.current);
        previewUpdateRafRef.current = null;
      }
      pendingPreviewRef.current = null;
    };
  }, [selectedPlayer, trackNumber, duration, setLoopStart, setLoopEnd, disableLoop, isLooping, loopStart, loopEnd, getPlayerLoopPoints, syncPoints, hasJumpedToSyncPoints]);

  // Re-register audio element if trackNumber changes (for reordering)
  useEffect(() => {
    const wavesurfer = wavesurferRef.current;
    if (!wavesurfer) return;
    const audioElement = wavesurfer.getMediaElement();
    if (!audioElement) return;
    registerAudio(trackNumber, audioElement);
    // Optionally, could unregister previous trackNumber if needed
    // (not strictly necessary if keys are unique and always up-to-date)
  }, [trackNumber, registerAudio]);

  const handleRemove = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
    }
    onRemove?.(trackNumber);
  }, [trackNumber, onRemove]);

  // Check if note has meaningful content (strip HTML and check for actual text)
  const hasNoteContent = (note: string) => {
    if (!note) return false;
    
    // Create a temporary element to strip HTML and get text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = note;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Check if there's any meaningful text after trimming whitespace
    return textContent.trim().length > 0;
  };

  const isSelected = selectedPlayer === trackNumber;
  const gainReduction = gainOffsets.get(trackNumber) || 1;
  const gainReductionDB = gainReduction !== 1 ? 
    (20 * Math.log10(gainReduction)).toFixed(1) : 
    '0.0';
  const isBlindTestIncluded = blindTestIncludedPlayers.get(trackNumber) ?? true;

  // Handle gain reset clicks
  const handleGainDisplayClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent player selection
    
    if (e.altKey) {
      // Option-click: Reset this player's gain
      resetGainForPlayer(trackNumber);
    } else if (e.metaKey) {
      // Command-click: Reset all players' gain
      resetAllGains();
    }
  }, [trackNumber, resetGainForPlayer, resetAllGains]);

  // Handle page visibility changes on mobile to prevent audio glitches
  useEffect(() => {
    if (!isMobileDevice()) return;
    
    const handleVisibilityChange = () => {
      if (document.hidden && isPlaying) {
        // Silently pause when page becomes hidden to prevent glitches
        pauseAll();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPlaying, pauseAll]);

  // Update interaction state when selection changes
  useEffect(() => {
    const wavesurfer = wavesurferRef.current;
    if (!wavesurfer) return;
    
    wavesurfer.setOptions({ interact: isSelected });
    
    // Only auto-scroll if there are more than 2 players, otherwise both players are visible
    if (isSelected && audioElements.size > 2) {
      const playerElement = document.querySelector(`[data-player-number="${trackNumber}"]`) as HTMLElement;
      if (playerElement) {
        console.log(`Player ${trackNumber} is selected, attempting to make it visible`);
        setTimeout(() => {
          try {
            // Find the scrollable container (the player list container)
            const scrollContainer = document.querySelector('[data-testid="player-container"]') as HTMLElement;
            
            if (scrollContainer) {
              // Calculate the position within the container
              const containerRect = scrollContainer.getBoundingClientRect();
              const playerRect = playerElement.getBoundingClientRect();
              const relativeTop = playerRect.top - containerRect.top;
              const scrollTop = scrollContainer.scrollTop + relativeTop - (containerRect.height / 2) + (playerRect.height / 2);
              
              // Smooth scroll within the container
              scrollContainer.scrollTo({
                top: scrollTop,
                behavior: 'smooth'
              });
            }
          } catch (error) {
            console.warn('Error during container scrolling:', error);
          }
        }, 50);
      }
    }
  }, [isSelected, trackNumber, audioElements.size]);

  // Add a timeupdate listener to update currentTime on any seek, even when paused
  useEffect(() => {
    const wavesurfer = wavesurferRef.current;
    if (!wavesurfer) return;
    const audioElement = wavesurfer.getMediaElement();
    if (!audioElement) return;

    const handleTimeUpdate = () => {
      setCurrentTime(audioElement.currentTime);
    };
    audioElement.addEventListener('timeupdate', handleTimeUpdate);

    // Set initial time in case it was changed externally
    setCurrentTime(audioElement.currentTime);

    return () => {
      audioElement.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [fileName]);

  return (
    <Box
      onClick={() => selectPlayer(trackNumber)}
      w="full"
      bg="gray.800"
      borderRadius="lg"
      overflow="hidden"
      transition="all 0.2s ease-in-out"
      transform={isSelected ? "scale(1.02)" : "scale(1)"}
      boxShadow={isDragged ? "0 0 20px rgba(49, 130, 206, 0.15)" : isSelected ? "0 0 20px rgba(255, 152, 0, 0.15)" : "none"}
      border="1px solid"
      borderColor={isDragged ? "#3182ce" : isSelected ? "accent.500" : "gray.700"}
      cursor="pointer"
      data-player-number={dataPlayerNumber}
      data-file-id={id}
      tabIndex={0}
      position="relative"
      onKeyDown={(e) => {
        if (e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          isPlaying ? pauseAll() : playAll();
        }
      }}
      css={{
        '&:focus': {
          outline: 'none',
          boxShadow: '0 0 0 2px rgba(66, 153, 225, 0.6)'
        }
      }}
      {...props}
    >
      <Box p={isEmbed ? 1.5 : 2}>
        {isEmbed ? (
          <Box position="relative" mb={0.5} px={1}>
            <HStack spacing={1} minW={0}>
              <Text 
                fontSize="xs" 
                fontWeight="medium"
                color={isSelected ? "accent.400" : "gray.300"}
                flexShrink={0}
              >
                {trackNumber}
              </Text>
              <Text 
                fontSize="xs" 
                color="gray.400"
                noOfLines={1}
                flex={1}
                minW={0}
              >
                {(() => {
                  if (!fileName) return 'No file loaded';
                  const customName = id ? getCustomDisplayName(id) : '';
                  const displayName = customName || fileName;
                  const sanitizedName = displayName.replace(/:/g, '/');
                  // Truncate to 60 characters with ellipsis
                  if (sanitizedName.length > 60) {
                    const truncated = sanitizedName.substring(0, 60).trimEnd();
                    return truncated + '...';
                  }
                  return sanitizedName;
                })()}
              </Text>
            </HStack>
            {/* Time readout - positioned on right */}
            <Box
              position="absolute"
              right="60px"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
            >
              <Text 
                fontSize="xs" 
                color="gray.400"
              >
                {formatTime(currentTime)} / {formatTime(duration)}
              </Text>
            </Box>
            {/* Right side: gain */}
            <Box position="absolute" right={0} top="50%" transform="translateY(-50%)">
              <Tooltip 
                label={
                  <VStack spacing={1} align="start" color="gray.800">
                    <HStack spacing={2}>
                      <Text>Reset gain</Text>
                      <TooltipKeyBox>⌥</TooltipKeyBox>
                      <Text>+ click</Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text>Reset all</Text>
                      <TooltipKeyBox>⌘</TooltipKeyBox>
                      <Text>+ click</Text>
                    </HStack>
                  </VStack>
                } 
                placement="top"
                hasArrow
              >
                <Text 
                  fontSize="xs" 
                  color="gray.400"
                  cursor="pointer"
                  flexShrink={0}
                  _hover={{ color: "gray.300" }}
                  onClick={handleGainDisplayClick}
                >
                  {gainReductionDB} dB
                </Text>
              </Tooltip>
            </Box>
          </Box>
        ) : (
          <HStack spacing={1} mb={1} px={1} justify="space-between">
            <HStack spacing={1}>
              <Text 
                fontSize="xs" 
                fontWeight="medium"
                color={isSelected ? "accent.400" : "gray.300"}
              >
                {trackNumber}
              </Text>
              {isEditingName && !isMobileDevice() && id ? (
                <Input
                  ref={nameInputRef}
                  value={editingNameValue}
                  onChange={(e) => setEditingNameValue(e.target.value)}
                  onBlur={() => {
                    if (id) {
                      const trimmed = editingNameValue.trim();
                      if (trimmed && trimmed !== fileName) {
                        setDisplayName(id, trimmed);
                      } else {
                        setDisplayName(id, '');
                      }
                    }
                    setIsEditingName(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    } else if (e.key === 'Escape') {
                      setEditingNameValue(originalNameRef.current);
                      setIsEditingName(false);
                    } else if (e.key === ' ') {
                      // Stop propagation of space key to prevent triggering playback
                      e.stopPropagation();
                    }
                  }}
                  fontSize="xs"
                  color="gray.400"
                  bg="gray.800"
                  border="1px solid"
                  borderColor="gray.600"
                  _focus={{
                    borderColor: "accent.400",
                    boxShadow: "0 0 0 1px var(--chakra-colors-accent-400)"
                  }}
                  flex={1}
                  autoFocus
                  size="sm"
                  h="auto"
                  py={0}
                  px={1}
                />
              ) : (
                <Text 
                  fontSize="xs" 
                  color="gray.400"
                  noOfLines={1}
                  flex={1}
                  onDoubleClick={() => {
                    if (!isMobileDevice() && id && fileName) {
                      const customName = getCustomDisplayName(id);
                      const displayName = customName || fileName;
                      originalNameRef.current = displayName;
                      setEditingNameValue(displayName);
                      setIsEditingName(true);
                    }
                  }}
                  cursor={!isMobileDevice() && id && fileName ? 'text' : 'default'}
                  title={!isMobileDevice() && id && fileName ? 'Double-click to edit name' : undefined}
                >
                  {(() => {
                    if (!fileName) return 'No file loaded';
                    const customName = id ? getCustomDisplayName(id) : '';
                    const displayName = customName || fileName;
                    return displayName.replace(/:/g, '/');
                  })()}
                </Text>
              )}
            </HStack>
            {!isEmbed && (
            <HStack spacing={2}>
              {(() => {
                const player = document.querySelector(`[data-player-number="${trackNumber}"]`);
                const isShareable = player?.hasAttribute('data-shareable-url');
                
                if (!isShareable) return null;
                
                return (
                  <Tooltip 
                    label={
                      hasExpandedPanel ? (
                        <HStack spacing={2} color="gray.800">
                          <Text>Edit Notes</Text>
                          {!isMobileDevice() && <TooltipKeyBox>N</TooltipKeyBox>}
                        </HStack>
                      ) : (
                        <VStack spacing={1} color="gray.800" align="start">
                          <HStack spacing={2}>
                            <Text>Edit Track Note</Text>
                            {!isMobileDevice() && <TooltipKeyBox>N</TooltipKeyBox>}
                          </HStack>
                          {!isMobileDevice() && (
                            <>
                              <HStack spacing={2}>
                                <Text>Add Timestamp</Text>
                                <TooltipKeyBox>⌘I</TooltipKeyBox>
                              </HStack>
                              <HStack spacing={2}>
                                <Text>Add Textstamp</Text>
                                <TooltipKeyBox>⇧⌘I</TooltipKeyBox>
                              </HStack>
                            </>
                          )}
                        </VStack>
                      )
                    } 
                    placement="top"
                  >
                    <IconButton
                      data-testid="notes-button"
                      aria-label="Add notes"
                      icon={<FaEdit size={15} />}
                      size="xs"
                      variant="ghost"
                      colorScheme="blue"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Check if expanded note panel is available first
                        const expandedNotePanel = (window as any).__expandedNotePanel;
                        if (expandedNotePanel && expandedNotePanel.trackNumber === trackNumber) {
                          // Use the expanded note panel for this track
                          expandedNotePanel.startEditing();
                        } else {
                          // Fall back to opening notes modal
                          onOpen();
                        }
                      }}
                      color={hasNoteContent(id ? getNote(id) : '') ? "accent.400" : "blue.400"}
                      _hover={{ color: "blue.300" }}
                    />
                  </Tooltip>
                );
              })()}
              <Tooltip 
                label={
                  <HStack spacing={2} color="gray.800">
                    <Text>Remove player</Text>
                    <TooltipKeyBox>⌫</TooltipKeyBox>
                  </HStack>
                } 
                placement="top"
              >
                <IconButton
                  aria-label="Remove player"
                  icon={<FaTrash />}
                  size="xs"
                  variant="ghost"
                  color="gray.400"
                  _hover={{ color: "gray.300" }}
                  onClick={handleRemove}
                />
              </Tooltip>
            </HStack>
          )}
        </HStack>
        )}

        <Box 
          ref={waveformRef}
          borderRadius="md"
          p={isEmbed ? 0.5 : 1}
          position="relative"
        >
          {((isLooping || loopState !== 'inactive') || (previewLoopStart !== null && previewLoopEnd !== null)) && (() => {
            // Use preview values during drag, otherwise use player-specific loop points or global loop points
            const playerLoopPoints = getPlayerLoopPoints(trackNumber);
            const effectiveLoopStart = previewLoopStart ?? (playerLoopPoints?.start ?? loopStart);
            const effectiveLoopEnd = previewLoopEnd ?? (playerLoopPoints?.end ?? loopEnd);
            const endTime = loopState === 'setting_end' ? currentTime : effectiveLoopEnd;
            const showHandles = isLooping && !isMobileDevice() && selectedPlayer === trackNumber && previewLoopStart === null;
            
            return (
              <>
                <Box
                  position="absolute"
                  top={0}
                  left={`${(effectiveLoopStart / duration) * 100}%`}
                  width={`${(endTime - effectiveLoopStart) / duration * 100}%`}
                  height="100%"
                  bg="accent.400"
                  opacity={0.25}
                  pointerEvents="none"
                  borderRadius="md"
                  transition={previewLoopStart !== null ? "none" : "all 0.2s ease-in-out"}
                  border="1px solid"
                  borderColor="accent.300"
                  _hover={{
                    opacity: 0.3
                  }}
                />
                {showHandles && (
                  <>
                    {/* Start handle */}
                    <Box
                      position="absolute"
                      top={0}
                      left={`${(effectiveLoopStart / duration) * 100}%`}
                      width="4px"
                      height="100%"
                      bg="accent.400"
                      cursor="ew-resize"
                      zIndex={10}
                      borderRadius="md"
                      borderLeft="2px solid"
                      borderColor="accent.200"
                      _hover={{
                        bg: "accent.300",
                        width: "6px"
                      }}
                      transition="all 0.1s ease-in-out"
                      transform="translateX(-2px)"
                    />
                    {/* End handle */}
                    <Box
                      position="absolute"
                      top={0}
                      left={`${(effectiveLoopEnd / duration) * 100}%`}
                      width="4px"
                      height="100%"
                      bg="accent.400"
                      cursor="ew-resize"
                      zIndex={10}
                      borderRadius="md"
                      borderRight="2px solid"
                      borderColor="accent.200"
                      _hover={{
                        bg: "accent.300",
                        width: "6px"
                      }}
                      transition="all 0.1s ease-in-out"
                      transform="translateX(-2px)"
                    />
                  </>
                )}
              </>
            );
          })()}

          <SyncMarker trackNumber={trackNumber} duration={duration} />
          
          {/* Timestamps and Textstamps overlay - dots above waveform */}
          {id && duration > 0 && timestampsAndTextstamps.length > 0 && (() => {
            const handleTimestampMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
              if (timestampOverlayRef.current) {
                const rect = timestampOverlayRef.current.getBoundingClientRect();
                setTimestampTooltipPosition({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top,
                });
              }
            };
            
            const handleTimestampMouseLeave = () => {
              setHoveredTimestamp(null);
              setTimestampTooltipPosition(null);
            };
            
            return (
              <>
                <Box
                  ref={timestampOverlayRef}
                  position="absolute"
                  top="-10px"
                  left={isEmbed ? "2px" : "4px"}
                  right={isEmbed ? "2px" : "4px"}
                  height="10px"
                  pointerEvents="none"
                  onMouseMove={handleTimestampMouseMove}
                  onMouseLeave={handleTimestampMouseLeave}
                >
                  {timestampsAndTextstamps.map((stamp, index) => {
                    const leftPercent = (stamp.time / duration) * 100;
                    
                    return (
                      <Box
                        key={`${stamp.type}-${stamp.time}-${index}`}
                        position="absolute"
                        left={`${leftPercent}%`}
                        width="10px"
                        height="10px"
                        display="flex"
                        alignItems="flex-end"
                        justifyContent="center"
                        pointerEvents="auto"
                        cursor="pointer"
                        transform="translateX(-50%)"
                        pb="1px"
                        onMouseEnter={() => setHoveredTimestamp(stamp)}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Seek to timestamp time
                          audioElements.forEach(audio => {
                            audio.currentTime = stamp.time;
                          });
                          // Highlight the corresponding timestamp in expanded notes view
                          const expandedNotePanel = (window as any).__expandedNotePanel;
                          if (expandedNotePanel && expandedNotePanel.trackNumber === trackNumber && expandedNotePanel.highlightTimestampAtTime) {
                            expandedNotePanel.highlightTimestampAtTime(stamp.time);
                          }
                        }}
                      >
                        <Box
                          width="5px"
                          height="5px"
                          borderRadius="full"
                          bg={stamp.type === 'timestamp' ? '#4299E1' : '#FF8C00'}
                          _hover={{
                            opacity: 0.8,
                          }}
                          transition="opacity 0.2s"
                        />
                      </Box>
                    );
                  })}
                </Box>
                
                {/* Custom tooltip that follows mouse */}
                {hoveredTimestamp && timestampTooltipPosition && timestampOverlayRef.current && (
                  <Box
                    position="absolute"
                    left={`${timestampTooltipPosition.x}px`}
                    top="8px"
                    transform="translateX(-50%)"
                    bg="white"
                    color="gray.800"
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="sm"
                    pointerEvents="none"
                    zIndex={1000}
                    whiteSpace="nowrap"
                    boxShadow="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    {hoveredTimestamp.type === 'timestamp' 
                      ? formatTime(hoveredTimestamp.time)
                      : hoveredTimestamp.text || formatTime(hoveredTimestamp.time)
                    }
                    <Box
                      position="absolute"
                      top="-4px"
                      left="50%"
                      transform="translateX(-50%)"
                      width={0}
                      height={0}
                      borderLeft="4px solid transparent"
                      borderRight="4px solid transparent"
                      borderBottom="4px solid"
                      borderBottomColor="white"
                    />
                    <Box
                      position="absolute"
                      top="-5px"
                      left="50%"
                      transform="translateX(-50%)"
                      width={0}
                      height={0}
                      borderLeft="4px solid transparent"
                      borderRight="4px solid transparent"
                      borderBottom="4px solid"
                      borderBottomColor="gray.200"
                    />
                  </Box>
                )}
              </>
            );
          })()}
          
          {/* Regions overlay - colored line below waveform */}
          {id && duration > 0 && (() => {
            const regions = getRegions(id);
            if (regions.length === 0) return null;
            
            const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
              if (regionOverlayRef.current) {
                const rect = regionOverlayRef.current.getBoundingClientRect();
                setTooltipPosition({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top,
                });
              }
            };
            
            const handleMouseLeave = () => {
              setHoveredRegion(null);
              setTooltipPosition(null);
            };
            
            return (
              <>
                <Box
                  ref={regionOverlayRef}
                  position="absolute"
                  bottom="-9px"
                  left={isEmbed ? "2px" : "4px"}
                  right={isEmbed ? "2px" : "4px"}
                  height="13px"
                  pointerEvents="none"
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                >
                  {regions.map((region, index) => {
                    const startPercent = (region.startTime / duration) * 100;
                    const endTime = region.endTime ?? duration;
                    const widthPercent = ((endTime - region.startTime) / duration) * 100;
                    
                    return (
                      <Box
                        key={`${region.startTime}-${index}`}
                        position="absolute"
                        left={`${startPercent}%`}
                        width={`${widthPercent}%`}
                        height="100%"
                        top="0"
                        pointerEvents="auto"
                        cursor="pointer"
                        role="group"
                        onMouseEnter={() => setHoveredRegion(region)}
                        onClick={(e) => {
                          e.stopPropagation();
                          // Store region data for editing
                          (window as any).__regionModalData = {
                            fileId: id,
                            currentTime: region.startTime,
                            hasOpenRegion: false,
                            editingRegion: region,
                          };
                          // Trigger modal open - we'll need to access the modal opener from window
                          const event = new CustomEvent('openRegionModal');
                          window.dispatchEvent(event);
                        }}
                      >
                        <Box
                          position="absolute"
                          top="5px"
                          left="0"
                          right="0"
                          height="3px"
                          bg={region.color}
                          borderRadius="sm"
                          pointerEvents="none"
                          _groupHover={{
                            opacity: 0.8,
                          }}
                          transition="opacity 0.2s"
                        />
                      </Box>
                    );
                  })}
                </Box>
                
                {/* Custom tooltip that follows mouse */}
                {hoveredRegion && tooltipPosition && regionOverlayRef.current && (
                  <Box
                    position="absolute"
                    left={`${tooltipPosition.x}px`}
                    bottom="8px"
                    transform="translateX(-50%)"
                    bg="white"
                    color="gray.800"
                    px={2}
                    py={1}
                    borderRadius="md"
                    fontSize="sm"
                    pointerEvents="none"
                    zIndex={1000}
                    whiteSpace="nowrap"
                    boxShadow="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    {hoveredRegion.name}
                    <Box
                      position="absolute"
                      bottom="-4px"
                      left="50%"
                      transform="translateX(-50%)"
                      width={0}
                      height={0}
                      borderLeft="4px solid transparent"
                      borderRight="4px solid transparent"
                      borderTop="4px solid"
                      borderTopColor="white"
                    />
                    <Box
                      position="absolute"
                      bottom="-5px"
                      left="50%"
                      transform="translateX(-50%)"
                      width={0}
                      height={0}
                      borderLeft="4px solid transparent"
                      borderRight="4px solid transparent"
                      borderTop="4px solid"
                      borderTopColor="gray.200"
                    />
                  </Box>
                )}
              </>
            );
          })()}
        </Box>

        {!isEmbed && (
          <HStack mt={1} px={2} position="relative">
            <Box w="full" textAlign="center">
              <Text 
                fontSize="xs" 
                color="gray.400"
              >
                {formatTime(currentTime)} / {formatTime(duration)}
              </Text>
            </Box>
            <Tooltip 
              label={
                <VStack spacing={1} align="start" color="gray.800">
                  <HStack spacing={2}>
                    <Text>Reset gain</Text>
                    <TooltipKeyBox>⌥</TooltipKeyBox>
                    <Text>+ click</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Text>Reset all</Text>
                    <TooltipKeyBox>⌘</TooltipKeyBox>
                    <Text>+ click</Text>
                  </HStack>
                </VStack>
              } 
              placement="top"
              hasArrow
            >
              <Text 
                fontSize="xs" 
                color="gray.400"
                position="absolute"
                right={2}
                cursor="pointer"
                _hover={{ color: "gray.300" }}
                onClick={handleGainDisplayClick}
              >
                {gainReductionDB} dB
              </Text>
            </Tooltip>
          </HStack>
        )}
      </Box>
      
      {/* Blind Test Inclusion Toggle */}
      {!isEmbed && (
        <Box position="absolute" bottom={1} left={2} zIndex={10}>
          <Tooltip 
            label={
              <VStack spacing={1} align="start" color="gray.800">
                <HStack spacing={2}>
                  <Text>Blind Test mode inclusion</Text>
                  <TooltipKeyBox>⌘B</TooltipKeyBox>
                </HStack>
                <HStack spacing={2}>
                  <Text>Toggle all:</Text>
                  <TooltipKeyBox>⇧B</TooltipKeyBox>
                </HStack>
              </VStack>
            } 
            placement="top"
            hasArrow
          >
            <IconButton
              aria-label="Toggle blind test inclusion"
              icon={<FaEyeSlash size={14} />}
              size="xs"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                toggleBlindTestIncluded(trackNumber);
              }}
              color={isBlindTestIncluded ? "accent.400" : "gray.500"}
              _hover={{ 
                color: isBlindTestIncluded ? "accent.300" : "gray.400",
                bg: "transparent"
              }}
              _active={{ 
                color: isBlindTestIncluded ? "accent.500" : "gray.300",
                bg: "transparent"
              }}
              bg="transparent"
              borderRadius="md"
            />
          </Tooltip>
        </Box>
      )}
      
      {!isEmbed && <NotesModal isOpen={isOpen} onClose={onClose} trackNumber={trackNumber} fileId={id} />}
    </Box>
  );
};

export default AudioPlayer; 